import { CardContent, Grid, Pagination, Typography } from "@mui/material";
import { MprcCard, MprcContainer, MprcIconButtonDropdown, MprcInfiniteLoading, MprcRequestErrorReport } from "components";
import { MprcFilterCountInfo } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { IconButtonBack } from "components/iconButtons";
import { IconSort } from "components/icons";
import { UserSearchDtoPaginatedList, UserSearchOrderBy } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { useGobackOrHome } from "views/common/hooks/useGobackOrHome";
import { GlobalSearchRowsPerPage } from "views/general/GlobalSearch/components/GlobalSearchTabs";
import { SearchRenderUser } from "views/general/GlobalSearch/components/SearchRenderUser";

/////////////////////////////////////////////////////////////////////////////////////////////////

export const FullUserSearch = () => {
    const [lastError, setLastError] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [orderBy, setOrderBy] = useState<UserSearchOrderBy>(UserSearchOrderBy.Relevance);
    const [globalSearchText, setGlobalSearchText] = useState<string>("");

    const userResult = useRef<UserSearchDtoPaginatedList>(null!);

    const { t } = useTranslation("shared");
    const [searchParams] = useSearchParams();
    const api = useCoreApi();
    const navigate = useNavigate();
    const goback = useGobackOrHome();

    ///
    const getState = () => {
        return { pageNumber, orderBy, globalSearchText };
    };

    ///
    async function loadUsers(pageNumber: number) {
        setLoading(true);
        setLastError(null);

        try {
            const response = await api.v1.globalSearch.userSearch({ searchText: globalSearchText, orderBy, pageNumber, pageSize: GlobalSearchRowsPerPage });
            userResult.current = response.data;
        }
        catch (e) {
            setLastError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ///
    const renderSearch = () => {

        if (loading) return null;

        return userResult.current.totalRows == 0
            ? <MprcFilterEmptyDataset message={t("GlobalSearch.FullSearch.NoUsersFound")} />
            : (
                <>
                    <MprcContainer>
                        <MprcFilterCountInfo
                            text={t("GlobalSearch.FullSearch.UsersFound", { count: userResult.current.totalRows })}
                        />
                    </MprcContainer>

                    <Grid container spacing={2}>
                        {
                            userResult.current.items.map((v, index) => {
                                return (
                                    <Grid key={index} size={{ xs: 4 }}>
                                        <SearchRenderUser
                                            data={v}
                                            onBeforeLinkClick={() => navigate(document.location.search, { replace: true, state: { state: getState() } })}
                                        />
                                    </Grid>
                                );
                            })
                        }
                    </Grid>

                    {
                        userResult.current.totalPages > 1 &&
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={userResult.current.pageNumber}
                            count={userResult.current.totalPages}
                            onChange={(e, pn) => setPageNumber(pn)}
                            sx={{ "& ul": { justifyContent: "center" } }}
                        />
                    }
                </>
            );
    };


    /////////////////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        const q = searchParams.get("q");
        setGlobalSearchText(q == null ? "" : decodeURIComponent(q));
        setPageNumber(1);
        setOrderBy(UserSearchOrderBy.Relevance);
    }, [document.location.search]);

    useDidUpdateEffect(() => {
        loadUsers(pageNumber);
    }, [orderBy, pageNumber, globalSearchText]);

    /////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        <div>
            <MprcInfiniteLoading visible={loading} />

            {
                lastError
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <MprcContainer>
                            <MprcCard>
                                <MprcCardFilterBox>
                                    <Grid container justifyContent="space-between" alignItems="center">
                                        <Grid>
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid>
                                                    <IconButtonBack onClick={goback} />
                                                </Grid>
                                                <Grid>
                                                    <Typography variant="h4">{t("GlobalSearch.FullSearch.PeopleTitle")}</Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid>
                                            <MprcIconButtonDropdown
                                                icon={<IconSort />}
                                                title={t("shared:Filter.OrderBy")}
                                                onChange={(orderBy) => setOrderBy(orderBy)}
                                                selected={orderBy}
                                                menuItemCheck
                                                items={
                                                    [
                                                        { text: t("GlobalSearch.Filters.UserOrderBy.Relevance"), value: UserSearchOrderBy.Relevance },
                                                        { text: t("GlobalSearch.Filters.UserOrderBy.Publications"), value: UserSearchOrderBy.Publications },
                                                        { text: t("GlobalSearch.Filters.UserOrderBy.Name"), value: UserSearchOrderBy.Name },
                                                    ]
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </MprcCardFilterBox>

                                <CardContent>
                                    {renderSearch()}
                                </CardContent>

                            </MprcCard>
                        </MprcContainer>
                    )
            }
        </div>
    );
};
